
import { useI18n } from 'locales';
import PropTypes from 'prop-types';
import { useCallback } from 'react';
import { Controller } from 'react-hook-form';
import Autocomplete from './Autocomplete';
import Checkbox from './Checkbox';
import CurrencyInput from './CurrencyInput';
import DatePicker from './DatePicker';
import DateTimePicker from './DateTimePicker';
import DropDownInput from './DropDownInput';
import DropZone from './DropZone';
import HtmlInput from './HtmlInput';
import TextInput from './TextInput';

function GeneralInput({
  name,
  type,
  label,
  placeholder,
  control,
  error,
  helper,
  registerOptions,
  ...props
}) {
  const t = useI18n();

  const render = useCallback(({ field } = {}) => {
    const translatedLabel = registerOptions && registerOptions.required
      ? t(label) + ' *'
      : t(label);
    const _helperText = t(error?.message ?? helper);

    let body = null;
    switch (type) {
      case 'autocomplete':
        body = (
          <Autocomplete
            label={translatedLabel}
            error={!!error}
            helper={_helperText}
            {...props}
            {...field}
          />
        );
        break;
      case 'select':
        body = (
          <DropDownInput
            label={translatedLabel}
            error={!!error}
            helper={_helperText}
            {...props}
            {...field}
          />
        );
        break;
      case 'html':
        body = (
          <HtmlInput
            error={!!error}
            helper={_helperText}
            label={translatedLabel}
            {...props}
            {...field}
          />
        );
        break;
      case 'hidden':
        body = null;
        break;
      case 'date':
        body = (
          <DatePicker
            label={translatedLabel}
            type={type}
            error={!!error}
            helper={_helperText}
            {...props}
            {...field}
          />
        );
        break;
      case 'datetime':
        body = (
          <DateTimePicker
            label={translatedLabel}
            type={type}
            error={!!error}
            helper={_helperText}
            {...props}
            {...field}
          />
        );
        break;
      case 'file':
        body = (
          <DropZone
            error={!!error}
            helper={_helperText}
            label={translatedLabel}
            {...props}
            {...field}
          />
        );
        break;
      case 'checkbox':
        body = (
          <Checkbox
            label={translatedLabel}
            error={!!error}
            helper={_helperText}
            {...props}
            {...field}
          />
        );
        break;
      case 'currency':
        body = (
          <CurrencyInput
            label={translatedLabel}
            error={!!error}
            helper={_helperText}
            {...props}
            {...field}
          />
        );
        break;
      default:
        body = (
          <TextInput
            label={translatedLabel}
            type={type}
            placeholder={placeholder}
            error={!!error}
            helperText={_helperText}
            {...props}
            {...field}
          />
        );
        break;
    }

    return body;

  }, [type, label, placeholder, props, error, helper, t, registerOptions]);

  if (!control) {
    return render();
  }

  return (
    <Controller
      name={name}
      control={control}
      rules={registerOptions}
      render={render}
    />
  );
}

GeneralInput.propTypes = {
  name: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  control: PropTypes.object,
  error: PropTypes.string,
  helper: PropTypes.string,
  readOnly: PropTypes.bool,
  registerOptions: PropTypes.object,
  options: PropTypes.array,
};

GeneralInput.defaultProps = {
  registerOptions: {},
};

export default GeneralInput;
